import { useState } from 'react';
import { Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import GroupsIcon from '@mui/icons-material/Groups';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import DescriptionIcon from '@mui/icons-material/Description';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import PhoneIcon from '@mui/icons-material/Phone';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import AddchartIcon from '@mui/icons-material/Addchart';
import ReportIcon from '@mui/icons-material/Report';
import { parentUrl } from '../../../utils/constants';
import Authorization from '../../../utils/Authorization';
import { useLocation, Link, useParams } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';

const HamburgerMenu = () => {
    const params = useParams();
    const memberId = params.memberId;
    const authData = new Authorization();
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const path = location.pathname.split('/').pop();
    const currentPath = location.pathname;
    const { chartDetails } = useSelector((state: RootState) => state.memberDemographicsDetails);

    const isMenuItemSelected = (itemLink: string) => {
        const relativePath = itemLink.replace(parentUrl, '/');
        return currentPath.startsWith(relativePath);
    };

    const isNestedPageSelected = (pagePath: string) => {
        return currentPath.includes(`/${pagePath}/`) || currentPath.endsWith(`/${pagePath}`);
    };

    const toggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setIsOpen(open);
    };

    const menuItems = [
        { text: 'Upcoming Appointments', link: `${parentUrl}provider/upcomingappointments`, icon: <CalendarMonthIcon /> },
        { text: 'Dashboard', link: `${parentUrl}provider/metricsummary`, icon: <DashboardIcon /> },
        authData.isInPermission('View members') && { text: 'Members', link: `${parentUrl}provider/members-list`, icon: <GroupsIcon /> },
        authData.isInPermission('View the requests') && { text: 'Requests', link: `${parentUrl}provider/requests`, icon: <FileOpenIcon /> },
        authData.isInPermission('View onboarding') && { text: 'Onboarding', link: `${parentUrl}provider/onboarding-workflow`, icon: <PhoneIcon /> },
        authData.isInPermission('View my schedule') && { text: 'My Schedule', link: `${parentUrl}provider/myschedule`, icon: <CalendarMonthIcon /> },
        authData.isInPermission('View all appointment') && { text: 'Schedules', link: `${parentUrl}provider/schedules`, icon: <CalendarMonthIcon /> },
        authData.isInPermission('View Reports') && { text: 'Reports', link: `${parentUrl}provider/reports`, icon: <DescriptionIcon /> },
        authData.isInPermission('View HIE') && { text: 'HIE', link: `${parentUrl}provider/hie`, icon: <LocalHospitalIcon /> },
        authData.isInPermission('View Scheduling') && { text: 'Office Scheduling', link: `${parentUrl}provider/officescheduling`, icon: <CalendarMonthIcon /> },
        authData.isInPermission('View Claims Page') && { text: 'Claims', link: `${parentUrl}provider/claims-management`, icon: <MedicalInformationIcon /> },
        authData.isInPermission('Update Hedis Metrics') && { text: 'Hedis Metrics', link: `${parentUrl}provider/hedis-metrics`, icon: <AddchartIcon /> },
    ].filter(Boolean);

    const isMemberChartRoute = location.pathname.includes('/provider/members-list/member-chart');

    const safetyPlanNeeded = chartDetails?.SafetyPlanNeeded;
    const planNeeded = safetyPlanNeeded ? 'Safety Plan Recommended' : '';
    const planIcon = (active) => {
        return (
            <Tooltip title={planNeeded} placement="right">
                <ReportIcon sx={{ color: active ? 'white' : 'error.main', marginLeft: '8px' }} />
            </Tooltip>
        );
    };

    const memberDetailPages = [
        { name: 'Performance', isVisible: true, path: 'performance' },
        {
            name: 'Assessments',
            isVisible: authData.isInPermission('View member assessment') || authData.isInPermission('Edit member assessment data'),
            path: 'assessment-dashboard',
        },
        {
            name: 'Integrated Care Plan',
            isVisible: true,
            path: 'integrated-care-plan',
            icon: planNeeded?.length > 0 ? planIcon(path === 'integrated-care-plan') : null,
        },
        {
            name: 'Scheduler',
            isVisible: authData.isInPermission('View member appointment') || authData.isInPermission('Add / Edit member appointment'),
            path: 'scheduling',
        },
        {
            name: 'Documents',
            isVisible: authData.isInPermission('View member documents') || authData.isInPermission('Manage member document'),
            path: 'documents',
        },
        { name: 'Timeline', isVisible: authData.isInPermission('View member Timeline'), path: 'timeline' },
        {
            name: 'Engagement',
            isVisible: authData.isInPermission('View engagements') || authData.isInPermission('Add / Edit engagements'),
            path: 'document-engagement',
        },
        {
            name: 'Education',
            isVisible: authData.isInPermission('View member education materials') || authData.isInPermission('Assign education materials to member'),
            path: 'education',
        },
        { name: 'Vital Signs', isVisible: authData.isInPermission('View member vital signs'), path: 'vital-signs' },
        { name: 'Requests', isVisible: authData.isInPermission('View the requests'), path: 'member-requests' },
        {
            name: 'Medication Management',
            isVisible: authData.isInPermission('View member assessment') || authData.isInPermission('Edit member assessment data'),
            path: 'medication-management',
        },
        { name: 'Clinical Data', isVisible: authData.isInPermission('Edit member clinical data'), path: 'clinical-data' },
        { name: 'Transitional Care', isVisible: true, path: 'transitional-care' },
        { name: 'HIE', isVisible: true, path: 'hie' },
        { name: 'Budget Form', isVisible: true, path: 'budget-form' },
        {
            name: 'Onboarding',
            isVisible: authData.isInPermission('View engagements') || authData.isInPermission('Add / Edit engagements'),
            path: 'onboarding-engagement',
        },
    ];

    const nestedPages = [
        { name: 'Member Profile', isVisible: true, path: 'member-profile', icon: null },
        ...memberDetailPages.filter((p) => p.isVisible).sort((a, b) => a.name.localeCompare(b.name)),
    ];

    return (
        <>
            <IconButton edge="start" color="inherit" aria-label="menu" onClick={toggleDrawer(!isOpen)} sx={{ borderRadius: 0, paddingLeft: '20px' }}>
                <MenuIcon />
            </IconButton>
            <Drawer anchor="left" open={isOpen} onClose={toggleDrawer(false)}>
                <div style={{ display: 'flex', paddingTop: '60px', minWidth: 320 }}>
                    <List
                        onClick={toggleDrawer(false)}
                        onKeyDown={toggleDrawer(false)}
                        sx={{
                            width: isMemberChartRoute ? 220 : 320,
                            '& .MuiListItem-root.Mui-selected, & .MuiListItem-root.Mui-selected:hover': {
                                backgroundColor: 'primary.main',
                                color: '#F7F7F7',
                                '& .MuiListItemText-primary': { color: '#F7F7F7' },
                                '& .MuiListItemIcon-root': { color: '#F7F7F7' },
                            },
                        }}
                    >
                        {menuItems.map((item, index) => (
                            <ListItem key={index} disablePadding>
                                <ListItemButton 
                                    component="a" 
                                    href={item.link} 
                                    selected={isMenuItemSelected(item.link)}
                                >
                                    <ListItemIcon>{item.icon}</ListItemIcon>
                                    <ListItemText primary={item.text} />
                                    {isMemberChartRoute && item.text === 'Members' ? <ChevronRightIcon /> : null}
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>

                    {isMemberChartRoute ? (
                        <List
                            onClick={toggleDrawer(false)}
                            onKeyDown={toggleDrawer(false)}
                            sx={{
                                width: 240,
                                borderLeft: '1px solid rgba(0,0,0,0.08)',
                                '& .MuiListItem-root.Mui-selected, & .MuiListItem-root.Mui-selected:hover': {
                                    backgroundColor: 'primary.main',
                                    color: '#F7F7F7',
                                    '& .MuiListItemText-primary': { color: '#F7F7F7' },
                                    '& .MuiListItemIcon-root': { color: '#F7F7F7' },
                                },
                            }}
                        >
                            {nestedPages.map((m, i) => (
                                <ListItem key={`md-${i}`} disablePadding>
                                    <ListItemButton
                                        component={Link}
                                        to={`${parentUrl}provider/members-list/member-chart/${memberId}/${m.path}`}
                                        selected={isNestedPageSelected(m.path)}
                                    >
                                        <ListItemText primary={m.name} />
                                        {m.icon ? m.icon : null}
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    ) : null}
                </div>
            </Drawer>
        </>
    );
};

export default HamburgerMenu;
